import React, { useEffect, useRef, useState } from 'react';

/**
 * VideoViewerModal - Modal para visualização de vídeos
 * Suporta: MP4, MOV, AVI, WebM, OGG
 * Player HTML5 nativo com controles completos
 */
function VideoViewerModal({ isOpen, onClose, videoUrl, videoTitle }) {
  const videoRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && videoRef.current) {
      videoRef.current.load();
      setError(null);
    }
  }, [isOpen, videoUrl]);

  // Fechar modal com ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Detectar fullscreen
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleVideoError = (e) => {
    console.error('Erro ao carregar vídeo:', e);
    setError('Não foi possível carregar o vídeo. Formato não suportado ou arquivo corrompido.');
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      videoRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4"
      style={{ zIndex: 10000 }}
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-6xl bg-gray-900 rounded-lg shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-gray-800 border-b border-gray-700">
          <h3 className="text-white font-semibold truncate flex-1 mr-4">
            🎬 {videoTitle || 'Vídeo'}
          </h3>
          
          <div className="flex items-center gap-2">
            <button
              onClick={toggleFullscreen}
              className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm transition-colors"
              title="Tela cheia"
            >
              ⛶
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-semibold transition-colors"
            >
              ✕ Fechar
            </button>
          </div>
        </div>

        {/* Video Player */}
        <div className="relative bg-black">
          {error ? (
            <div className="flex flex-col items-center justify-center p-8 min-h-[400px] text-center">
              <span className="text-6xl mb-4">⚠️</span>
              <p className="text-white text-lg mb-2">Erro ao carregar vídeo</p>
              <p className="text-gray-400 text-sm max-w-md">{error}</p>
            </div>
          ) : (
            <video
              ref={videoRef}
              className="w-full max-h-[70vh] object-contain"
              controls
              controlsList="nodownload"
              onError={handleVideoError}
              style={{ backgroundColor: '#000' }}
            >
              <source src={videoUrl} type="video/mp4" />
              <source src={videoUrl} type="video/quicktime" />
              <source src={videoUrl} type="video/webm" />
              <source src={videoUrl} type="video/ogg" />
              Seu navegador não suporta a reprodução de vídeo.
            </video>
          )}
        </div>

        {/* Footer - Info */}
        <div className="p-3 bg-gray-800 text-gray-400 text-xs text-center">
          Utilize os controles do player para: Play/Pause, Volume, Ajustar tempo, Tela cheia
        </div>
      </div>
    </div>
  );
}

export default VideoViewerModal;
